import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Sprout } from 'lucide-react'
import { billingApi, type UpsellOffer } from '../../lib/api'
import { apiError } from '../../lib/errors'

// Local design tokens → CSS vars so the modal theme-flips (light/dark). Accent +
// status stay literal hex (read fine on both modes; used in color-mix). §83/§85.
const C = {
  card: 'var(--color-bg-card)',
  card2: 'var(--color-bg-card-2)',
  border: 'var(--color-border)',
  amber: 'var(--color-primary)',
  green: '#4CAF50',
  red: '#F87171',
  text: 'var(--color-text)',
  textMuted: 'var(--color-text-muted)',
  textFaint: 'var(--color-text-faint)',
}

/**
 * The plant-limit upgrade modal (§242). Shown by <UpsellGate/> either proactively
 * (sustained ≥2 days at the cap) or reactively (a plant create 402'd). Pricing is
 * dynamic from the offer (backend reads billing.AllPlans), so copy never drifts.
 * onClose(snoozed) — snoozed=true suppresses the proactive auto-show for a few days.
 */
export function UpgradeModal({ offer, onClose }: { offer: UpsellOffer; onClose: (snoozed: boolean) => void }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [err, setErr] = useState('')

  // Escape closes (WCAG) — treated as "maybe later" (snooze).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(true) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const roomText = offer.suggested_limit > 0
    ? t('upsell.up_to', 'up to {{n}} plants', { n: offer.suggested_limit.toLocaleString() })
    : t('upsell.unlimited', 'unlimited plants')

  const upgrade = useMutation({
    mutationFn: () => billingApi.changePlan(offer.suggested_plan!).then((r) => r.data),
    onSuccess: (res) => {
      if (res?.error) { setErr(res.error); return }
      if (res?.mode === 'checkout' && res?.url) { window.location.href = res.url; return } // hosted Checkout
      // switched / noop — the tenant now has room; refresh the world + close.
      qc.invalidateQueries({ queryKey: ['upsell'] })
      qc.invalidateQueries({ queryKey: ['subscription'] })
      qc.invalidateQueries({ queryKey: ['plants'] })
      onClose(false)
    },
    onError: (e) => setErr(apiError(e, t('upsell.upgrade_failed', 'Could not start the upgrade — please try again.'))),
  })

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose(true)}
      style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      role="dialog" aria-modal="true" aria-labelledby="upsell-title"
    >
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, width: 440, maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${C.border}` }}>
          <h2 id="upsell-title" style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text, fontFamily: "'Syne', sans-serif" }}>
            {t('upsell.title', "You've reached your plant limit")}
          </h2>
          <button onClick={() => onClose(true)} aria-label={t('common.close', 'Close')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textFaint }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: 20, overflowY: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 10, background: C.card2, marginBottom: 16 }}>
            <Sprout size={20} style={{ color: C.amber, flexShrink: 0 }} aria-hidden="true" />
            <div style={{ fontSize: 13, color: C.text }}>
              {t('upsell.usage', 'Your {{plan}} plan covers {{limit}} plants — you have {{have}}.', {
                plan: offer.current_plan_name, limit: offer.limit.toLocaleString(), have: offer.count.toLocaleString(),
              })}
            </div>
          </div>

          {offer.suggested_plan ? (
            <>
              <p style={{ margin: '0 0 14px', fontSize: 13.5, color: C.textMuted, lineHeight: 1.5 }}>
                {t('upsell.gain', 'Upgrade to {{plan}} for {{room}} — plus everything your plan does now.', {
                  plan: offer.suggested_plan_name, room: roomText,
                })}
              </p>

              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: offer.is_founding ? 10 : 18 }}>
                <span style={{ fontSize: 26, fontWeight: 800, color: C.text, fontFamily: "'Syne', sans-serif" }}>${offer.price_monthly_usd}</span>
                <span style={{ fontSize: 13, color: C.textFaint }}>/{t('upsell.mo', 'mo')}</span>
                {offer.price_yearly_usd ? (
                  <span style={{ fontSize: 12.5, color: C.textMuted, marginLeft: 4 }}>{t('upsell.or_year', 'or ${{p}}/year', { p: offer.price_yearly_usd })}</span>
                ) : null}
              </div>

              {offer.is_founding && (
                <div style={{ fontSize: 12.5, color: C.green, background: 'color-mix(in srgb, #4CAF50 12%, transparent)', border: '1px solid color-mix(in srgb, #4CAF50 30%, transparent)', borderRadius: 8, padding: '8px 12px', marginBottom: 18 }}>
                  {t('upsell.founding', "As a founding member, this rate locks in when you upgrade — it won't rise later.")}
                </div>
              )}

              {err && <div role="alert" style={{ fontSize: 12.5, color: C.red, marginBottom: 12 }}>{err}</div>}

              <button
                onClick={() => upgrade.mutate()}
                disabled={upgrade.isPending}
                style={{ width: '100%', padding: 11, borderRadius: 9, border: 'none', cursor: upgrade.isPending ? 'default' : 'pointer', background: C.amber, color: '#0E1209', fontSize: 14, fontWeight: 700, opacity: upgrade.isPending ? 0.7 : 1 }}
              >
                {upgrade.isPending ? t('upsell.upgrading', 'Starting upgrade…') : t('upsell.upgrade_to', 'Upgrade to {{plan}}', { plan: offer.suggested_plan_name })}
              </button>
              <button onClick={() => onClose(true)} style={{ width: '100%', padding: 9, marginTop: 8, borderRadius: 9, border: `1px solid ${C.border}`, cursor: 'pointer', background: 'none', color: C.textMuted, fontSize: 13 }}>
                {t('upsell.later', 'Maybe later')}
              </button>
            </>
          ) : (
            <p style={{ margin: 0, fontSize: 13.5, color: C.textMuted }}>
              {t('upsell.top_plan', "You're on our top plan — you already have unlimited plants.")}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
